/**
 * Sidebar Tabs Component
 *
 * Handles tab switching between Filters and Collections panels
 * with localStorage persistence for user preference.
 *
 * @component sidebar-tabs-component
 */

import { Component } from '@theme/component';

const STORAGE_KEY = 'soldered_sidebar_tab';
const DEFAULT_TAB = 'filters';

/**
 * @typedef {Object} SidebarTabsComponentRefs
 * @property {HTMLButtonElement} tabFilters - The filters tab button.
 * @property {HTMLButtonElement} tabCollections - The collections tab button.
 * @property {HTMLElement} panelFilters - The filters panel element.
 * @property {HTMLElement} panelCollections - The collections panel element.
 */

/**
 * @extends {Component<SidebarTabsComponentRefs>}
 */
export default class SidebarTabsComponent extends Component {
  constructor() {
    super();
    this.activeTab = DEFAULT_TAB;
  }

  connectedCallback() {
    super.connectedCallback();

    this.restoreTabState();

    this.refs.tabFilters?.addEventListener('click', () => this.switchTab('filters'));
    this.refs.tabCollections?.addEventListener('click', () => this.switchTab('collections'));
  }

  restoreTabState() {
    try {
      const savedTab = localStorage.getItem(STORAGE_KEY);

      if (savedTab && (savedTab === 'filters' || savedTab === 'collections')) {
        this.activeTab = savedTab;
      } else {
        this.activeTab = DEFAULT_TAB;
      }

      this.updateTabDisplay();
    } catch (error) {
      console.warn('Could not restore sidebar tab state:', error);
      this.activeTab = DEFAULT_TAB;
      this.updateTabDisplay();
    }
  }

  /**
   * Switch to a specific tab
   * @param {string} tabName - 'filters' or 'collections'
   */
  switchTab(tabName) {
    if (tabName !== 'filters' && tabName !== 'collections') {
      console.warn(`Invalid tab name: ${tabName}`);
      return;
    }

    this.activeTab = tabName;

    this.saveTabState(tabName);

    this.updateTabDisplay();
  }

  /**
   * Save tab state to localStorage
   * @param {string} tabName - The tab to save
   */
  saveTabState(tabName) {
    try {
      localStorage.setItem(STORAGE_KEY, tabName);
    } catch (error) {
      console.warn('Could not save sidebar tab state:', error);
    }
  }

  updateTabDisplay() {
    const isFiltersActive = this.activeTab === 'filters';
    const isCollectionsActive = this.activeTab === 'collections';

    if (this.refs.tabFilters) {
      this.refs.tabFilters.setAttribute('aria-selected', isFiltersActive.toString());
    }

    if (this.refs.tabCollections) {
      this.refs.tabCollections.setAttribute('aria-selected', isCollectionsActive.toString());
    }

    if (this.refs.panelFilters) {
      if (isFiltersActive) {
        this.refs.panelFilters.classList.remove('sidebar-tabs__panel--hidden');
      } else {
        this.refs.panelFilters.classList.add('sidebar-tabs__panel--hidden');
      }
    }

    if (this.refs.panelCollections) {
      if (isCollectionsActive) {
        this.refs.panelCollections.classList.remove('sidebar-tabs__panel--hidden');
      } else {
        this.refs.panelCollections.classList.add('sidebar-tabs__panel--hidden');
      }
    }
  }
}

if (!customElements.get('sidebar-tabs-component')) {
  customElements.define('sidebar-tabs-component', SidebarTabsComponent);
}
